import React, { useState, useEffect, useContext } from "react";

// MUI
import styled from "@emotion/styled";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

// components
import CustomButton from "../Common/CustomButton";
import CustomTextField from "../Common/CustomTextField";
import AutoCompleteMultiState from "../Common/AutoComplete/AutoCompleteMultiState";
import AutoCompleteMultiRole from "../Common/AutoComplete/AutoCompleteMultiRole";

// apis
import { addUsers } from "../../apis/users";
import { getPropertyList } from "../../apis/data";

// context
import { AlertContext } from "../../contexts/AlertContext";
import { DataContext } from "../../contexts/DataContext";

const ModalContainer = styled("div")(({ theme }) => ({
  background: theme.palette.primary.light,
  borderRadius: "14px",
  padding: "1.5rem 2rem",
  overflowY: "scroll",
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: `${theme.palette.secondary.main}`,
  },

  "& .radio_group": {
    flex: "0.8",
  },
  "& .option_container": {
    display: "flex",
    alignItems: "center",
    marginBottom: "1.3rem",
  },
  "& .option_name": {
    width: "25%",
    fontSize: "16px",
    color: "#4F4F4F",
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
    },
  },
  "& .option_input": {
    width: "75%",
    display: "flex",
    gap: "0.5rem",
    "& .MuiInputBase-root": {
      height: "36px",
    }
  },
  "& .option_input_smar_group": {
    width: "70%",
    display: "flex",
    gap: "0.5rem",
  },
  "& .option_input_last": {
    width: "75%",
    display: "flex",
    gap: "0.5rem",
  },
  "& .button_style": {
    borderRadius: "20px",
    color: "#FFFFFF",
    background: theme.palette.primary.main,
    // width: "20%",
    fontSize: "16px",
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
    },
  },
  "& .cancel_style": {
    borderRadius: "20px",
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    width: "20%",
    fontSize: "16px",
    fontWeight: "500",
    [theme.breakpoints.down("xl")]: {
      fontSize: "14px",
    },
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: "8px",
  padding: "5px",
  "& .editIcon": {
    fontSize: "1.4rem",
    color: "#4F4F4F",
    background: "#EFFFFD",
    [theme.breakpoints.down("xl")]: {
      fontSize: "1.2rem",
    },
  },
  "& .delIcon": {
    fontSize: "1.4rem",
    color: "#FF5252",
    background: "#EFFFFD",
    [theme.breakpoints.down("xl")]: {
      fontSize: "1.2rem",
    },
  },
}));

const initialState = {
  id: "",
  name: "",
  email: "",
  phone_number: "",
  state_ids: [],
  role_ids: { id: "", name: "" },
  project_ids: [],
  property_ids: [],
  access_ids: [],

};

const AddUser = ({
  handleClose,
  modalData,
  action,
  modalProps,
  handleEdit,
  handleUpdate,
}) => {
  const [state, setState] = useState(initialState);
  const [selectedProjectList, setSelectedProjectList] = useState([]);
  const [selectedRoleList, setSelectedRoleList] = useState([]);
  const [selectedPropertyList, setSelectedPropertyList] = useState([]);
  const [selectedAccess, setSelectedAccess] = useState([]);
  const [propertyList, setPropertyList] = useState([]);
  const [projectPropertyMap, setProjectPropertyMap] = useState({});
  const { projectList, userRoles } = useContext(DataContext);
  const { showAlert } = useContext(AlertContext);
  const { accessList } = useContext(DataContext);



  useEffect(() => {
    if (modalData) {
      setState({
        id: modalData?.pk || "",
        name: modalData?.name || "",
        email: modalData?.email || "",
        phone_number: modalData?.phone_number || "",
        state_ids: modalData?.state || [],
        role_ids: modalData?.role
          ? modalData?.role.length === 1
            ? modalData?.role[0]
            : modalData?.role
          : { id: "", name: "" },
        project_ids: modalData?.project || [],
        property_ids: modalData?.property || [],
        access_ids: modalData?.access_ids,
      });

      if (modalData?.project?.length) {
        getPropertyData(modalData.project.map(p => p.id));
      }
    }
  }, [modalData]);

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const getPropertyData = async (proj = []) => {
    if (!proj.length) {
      setProjectPropertyMap({});
      setPropertyList([]);
      setState(prev => ({ ...prev, property_ids: [] }));
      return {};
    }

    const newMap = {};
    for (const projId of proj) {
      const { result, err } = await getPropertyList([projId]);
      if (err) {
        alert("Couldn't fetch the data");
        newMap[projId] = [];
        continue;
      }
      newMap[projId] = Array.isArray(result) ? result : [];
    }

    setProjectPropertyMap(newMap);
    const allProps = Object.values(newMap).flat();
    setPropertyList(allProps);

    return newMap;
  };

  const handleChange = (data, text, reason) => {
    if (text === "project") {
      const ids = (data || []).map(d => d.id);
      setSelectedProjectList(ids);

      if (ids.length === 0) {
        setProjectPropertyMap({});
        setPropertyList([]);
        setSelectedPropertyList([]);
        setState(prev => ({ ...prev, project_ids: [], property_ids: [] }));
        return;
      }

      getPropertyData(ids).then((mapByProject) => {
        const allowedProps = ids.flatMap(pid => mapByProject[pid] || []);
        const allowedIds = new Set(allowedProps.map(p => p.id));

        setState(prev => ({
          ...prev,
          project_ids: data,
          property_ids: prev.property_ids.filter(p => allowedIds.has(p.id)),
        }));
      });
      return;
    }

    if (text === "property") {
      const ids = (data || []).map(d => d.id);
      setSelectedPropertyList(ids);
      setState(prev => ({ ...prev, property_ids: data || [] }));
      return;
    }

    if (text === "roles") {
      const ids = (data || []).map(d => d.id);
      setSelectedRoleList(ids);
      return;
    }
    if (text === "access") {
      const ids = (data || []).map(d => d.id);
      setSelectedAccess(ids);
      setState(prev => ({ ...prev, access_ids: data || [] })); 
    }


  };

  const handleChangeRole = (data, text) => {
    if (data) {
      setState(prevState => ({
        ...prevState,
        [`${text}`]: data,
      }));
      setSelectedRoleList([data.id]);
    }
  };

  const handleDeleteProject = (data, type) => {
    if (type === "property") {
      const newProps = state.property_ids.filter(item => item.id !== data.id);
      setState(prev => ({ ...prev, property_ids: newProps }));
      setSelectedPropertyList(newProps.map(p => p.id));
      return;
    }

    if (type === "project") {
      const newProjects = state.project_ids.filter(item => item.id !== data.id);

      const updatedMap = { ...projectPropertyMap };
      delete updatedMap[data.id];
      setProjectPropertyMap(updatedMap);

      if (newProjects.length === 0) {
        setState(prev => ({ ...prev, project_ids: [], property_ids: [] }));
        setSelectedProjectList([]);
        setSelectedPropertyList([]);
        setPropertyList([]);
        return;
      }

      const allProps = Object.values(updatedMap).flat();
      const allowedIds = new Set(allProps.map(p => p.id));

      setPropertyList(allProps);
      setState(prev => ({
        ...prev,
        project_ids: newProjects,
        property_ids: prev.property_ids.filter(p => allowedIds.has(p.id)),
      }));
      setSelectedProjectList(newProjects.map(p => p.id));
      setSelectedPropertyList(prev => prev.filter(id => allowedIds.has(id)));
    }

    if (type === "access") {
      const newAccess = state.access_ids.filter(item => item.id !== data.id);
      setState(prev => ({ ...prev, access_ids: newAccess }));
      setSelectedAccess(newAccess.map(a => a.id));
      return;
    }

  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    let accessData = [];
    if (state.access_ids.length) {
      accessData = state.access_ids.map(a => a.id);
    } else if (selectedAccess.length > 0) {
      accessData = [...selectedAccess];
    }
     

    let rolesData = [];
    if (selectedRoleList.length > 0) {
      rolesData = [...selectedRoleList];
    } else {
      if (state.role_ids?.id) {
        rolesData = [state.role_ids?.id];
      }
    }

    let projectData = [];
    if (state.project_ids.length) {
      let ids = state.project_ids.map(item => (item.id));
      projectData = [...new Set([...selectedProjectList, ...ids])];
    } else {
      if (selectedProjectList.length > 0) {
        projectData = [...selectedProjectList];
      }
    }

    let propertyData = [];
    if (state.property_ids.length) {
      let ids = state.property_ids.map(item => (item.id));
      propertyData = [...new Set([...selectedPropertyList, ...ids])];
    } else {
      if (selectedPropertyList.length > 0) {
        propertyData = [...selectedPropertyList];
      }
    }

    const { status, message } = await addUsers(
      state.id,
      state.name,
      state.email,
      state.phone_number,
      rolesData,
      projectData,
      propertyData,
      accessData,
      action
    );
    if (status) {
      if (action === "edit") {
        showAlert("User Info edited successfully", "success");
      } else {
        showAlert("User added successfully", "success");
      }

    } else if (status === 400) {
      showAlert(message, "error");
    }
    setState(initialState);
    if (action === "edit") {
      handleUpdate();
      handleEdit();
    }
    if (action === "create") {
      modalProps.setRefreshList(!modalProps.refreshList);
      handleClose();
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ModalContainer
        style={{ padding: action === "edit" ? "1.2rem 0rem" : "1.5rem 2rem" }}
      >
        <div className="option_container">
          <span className="option_name">Name</span>
          <div className="option_input">
            <CustomTextField
              value={state.name}
              name="name"
              label="Enter Name"
              variant="outlined"
              onChange={handleTextChange}
              sx={{ width: "80%" }}
              required
            />
          </div>
        </div>
        {action === "create" ?
          <div className="option_container">
            <span className="option_name">Email ID</span>
            <div className="option_input">
              <CustomTextField
                value={state.email}
                name="email"
                label="Enter Email Id"
                variant="outlined"
                onChange={handleTextChange}
                sx={{ width: "80%" }}
                required
              />
            </div>
          </div> :
          <></>
        }

        <div className="option_container">
          <span className="option_name">Phone</span>
          <div className="option_input">
            <CustomTextField
              value={state.phone_number}
              name="phone_number"
              label="Enter Phone Number"
              variant="outlined"
              onChange={handleTextChange}
              sx={{ width: "80%" }}
              required
            />
          </div>
        </div>

        {action === "edit" &&
          state.project_ids.map((item, index) => {
            return (
              <div className="option_container">
                <span className="option_name">{index === 0 ? "Project" : ""}</span>
                <div className="option_input_smar_group">
                  <CustomTextField
                    value={item.name}
                    name="page_name"
                    label="Page"
                    variant="outlined"
                    onChange={handleTextChange}
                    sx={{ width: "86%" }}
                    disabled
                  />
                </div>
                <StyledIconButton
                  disableRipple={true}
                  onClick={() => handleDeleteProject(item, "project")}
                >
                  <DeleteOutlineOutlinedIcon className="delIcon" />
                </StyledIconButton>
              </div>
            )
          })
        }

        <div className="option_container">
          <span className="option_name">{action === "edit" ? "Add more Project(s)" : "Project(s)"}</span>
          <div className="option_input_last">
            <FormControl sx={{ width: "80%" }}>
              <AutoCompleteMultiState
                size="small"
                label="Select Project"
                limitTags={1}
                options={projectList}
                text="project"
                value={state.project_ids}
                onChange={handleChange}
              />
            </FormControl>
          </div>
        </div>

        {action === "edit" &&
          state.property_ids.map((item, index) => {
            return (
              <div className="option_container">
                <span className="option_name">{index === 0 ? "Property" : ""}</span>
                <div className="option_input_smar_group">
                  <CustomTextField
                    value={item.name}
                    name="property"
                    label="Property"
                    variant="outlined"
                    onChange={handleTextChange}
                    sx={{ width: "86%" }}
                    disabled
                  />
                </div>
                <StyledIconButton
                  disableRipple={true}
                  onClick={() => handleDeleteProject(item, "property")}
                >
                  <DeleteOutlineOutlinedIcon className="delIcon" />
                </StyledIconButton>
              </div>
            )
          })
        }

        <div className="option_container">
          <span className="option_name">{action === "edit" ? "Add more Property(s)" : "Property(s)"}</span>
          <div className="option_input_last">
            <FormControl sx={{ width: "80%" }}>
              <AutoCompleteMultiState
                size="small"
                label="Select Property"
                limitTags={1}
                options={propertyList.filter(
                  (prop) => state.project_ids.some((proj) => proj.id === prop.project_id)
                )}
                text="property"
                value={state.property_ids}
                onChange={handleChange}
              />
            </FormControl>
          </div>
        </div>
        <div className="option_container">
          <div className="option_name">Role</div>
          <div className="option_input_last">
            <FormControl sx={{ width: "80%" }}>
              <AutoCompleteMultiRole
                size="small"
                variant="outlined"
                text="role_ids"
                value={state.role_ids}
                options={userRoles}
                autoComplete="true"
                label="Select Role"
                onChange={handleChangeRole}
              />
            </FormControl>
          </div>
        </div>

        {action === "edit" &&
          state.access_ids.map((item, index) => (
            <div className="option_container" key={item.id}>
              <span className="option_name">{index === 0 ? "Access" : ""}</span>
              <div className="option_input_smar_group">
                <CustomTextField
                  value={item.name}
                  name="access"
                  label="Access"
                  variant="outlined"
                  sx={{ width: "86%" }}
                  disabled
                />
              </div>
              <StyledIconButton
                disableRipple={true}
                onClick={() => handleDeleteProject(item, "access")}
              >
                <DeleteOutlineOutlinedIcon className="delIcon" />
              </StyledIconButton>
            </div>
          ))
        }

        <div className="option_container">
          <span className="option_name">
            {action === "edit" ? "Add more Access" : "Access"}
          </span>
          <div className="option_input_last">
            <FormControl sx={{ width: "80%" }}>
              <AutoCompleteMultiState
                size="small"
                label="Select Access"
                limitTags={1}
                options={accessList}
                text="access"
                value={state.access_ids}
                onChange={handleChange}
              />
            </FormControl>
          </div>
        </div>


        {action === "edit" ? (
          <div className="option_container">
            <div className="option_name"></div>
            <div className="option_input">
              <CustomButton
                variant="contained"
                className="button_style"
                type="submit"
              >
                Save
              </CustomButton>
              <CustomButton
                variant="outlined"
                className="cancel_style"
                onClick={handleEdit}
              >
                Cancel
              </CustomButton>
            </div>
          </div>
        ) : (
          <div className="option_container">
            <div className="option_name"></div>
            <div className="option_input">
              <CustomButton
                variant="contained"
                sx={{
                  color: "white",
                  borderRadius: "1rem",
                  textTransform: "none",
                }}
                type="submit"
              >
                Create an User
              </CustomButton>
            </div>
          </div>
        )}

      </ModalContainer>
    </form>
  );
};

export default AddUser;
